import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {
    ALGORITHM_CAPPED_VAI,
    ALGORITHM_THOUSANDTH,
    ALGORITHM_VAI,
    ECEconomicsFanoRedistributionSettings,
    ECEconomicsMixedRedistributionSettings,
    ECEconomicsRedistributedIncentivation,
    ECEconomicsRedistributedMember,
    ECEconomicsRedistributedPerformance,
    ECEconomicsRedistributedResponse, ECEconomicsStrategyFeatures,
    ECO_ERROR_PARAM_KEY,
    EconomicsErrorType,
    REDISTRIBUTION_WARNING_PARTIALLY_RELEVANT_MEMBERS, STRATEGY_OLD_CONDO
} from 'smag-commons/dto/eco';
import {Router} from '@angular/router';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {
    CAP as CAP_KEY,
    EC_FILE_MIXED,
    EC_FILE_THOUSANDTHS,
    ECEconomicsTemplateType,
    ECMember,
    ECMemberSubjectClassification,
    ECMode,
    EnergyCommunity,
    EntityType,
    Pod,
    THOUSANDTHS
} from 'smag-commons/cfg/hierarchy';
import {
    catchError,
    distinctUntilChanged,
    filter,
    first,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import {
    ALGORITHM_CONDO_OLD,
    ALGORITHM_FANO,
    ALGORITHM_MIXED,
    ALGORITHM_NO_ALGORITHM,
    ECEconomicsCappedVAIRedistributionSettings,
    ECEconomicsFanoRedistributedPodIncentivationAlgorithm, ECEconomicsFeatures,
    ECEconomicsPerformance,
    ECEconomicsRedistributedPod,
    ECEconomicsRedistributionSettings,
    ECEconomicsSurplusPremiumRate,
    ECEconomicsThousandthRedistributionSettings,
    ECEconomicsVAIRedistributionSettings,
    ECPodDataSource,
    ECSurplusPremiumRate,
    ECSurplusPremiumRateScope,
    REDISTRIBUTION_WARNING_MIXED_INSUFFICIENT_REVENUE
} from 'smag-commons/dto/eco/community';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {I18NService} from '../../../i18n/i18n.service';
import {EconomicsError} from 'smag-commons/dto/eco/errors';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {ValueObjectType} from 'smag-commons/cfg/value';
import {SelectedAlgorithm} from './algorithm/ec-algorithm.component';
import * as _ from 'lodash';
import {
    COSTS,
    EC_PLANTS_REI_REDISTRIBUTION,
    PLANT_TIP_QUOTE,
    REDISTRIBUTION_SETTINGS, STRATEGY,
    SURPLUS_PREMIUM_RATE_MODE
} from 'smag-commons/cfg/economics-values';
import {EnergyCommunityNetwork, Permission} from 'smag-commons/dto/api';
import {MatDialog} from '@angular/material/dialog';
import {CAP} from './cap-selector/cap-selector.component';
import {isConsumer, isProsumer} from 'smag-commons/fcn';
import {EconomicsErrorCode} from '../../../api/errors';
import {can} from '../../../shared/permissions';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {ConfigurationService} from '../../../shared/configuration.service';
import {downloadBlob, errorCode, isDefined, isNumber} from '../../../commons/commons';
import {SmagApiService} from '../../../api/smag-api.service';
import {mkMode} from '../../../shared/energy-community';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {
    CACV_STRING,
    CHAD_DIALOG_MAX_WIDTH,
    CHAD_DIALOG_MIN_WIDTH,
    ChartOptions,
    DEFAULT_DIALOG_MAX_WIDTH,
    DEFAULT_DIALOG_MIN_WIDTH,
    DEFAULT_PAGE_SIZE,
    DEFAULT_PAGE_SIZES,
    disableDataLabels,
    disableLegend,
    getAlgoDialogWidth,
    getNiceTooltip,
    getPodType,
    getSubjectName,
    getTranslatedLabels,
    GIGA_CHAD_DIALOG_MAX_WIDTH,
    isUpdatable,
    isViewable,
    isWholeYearSelected,
    keepYourSecrets,
    mkAbsorptionParticipationChart,
    mkBillingPeriodParams,
    mkConsolidatedMonths,
    mkPersonalSharingChart,
    mkYearlyValue,
    sanityFromTimeActivationDate,
    sanityToTime,
    showNullData,
    YearSummaryConsolidation
} from '../../../shared/ec-economics';
import {NiceValuePipe} from '../../../shared/shared.pipe';
import {environment} from '../../../../environments/environment';
import {YearlyValue} from 'smag-commons/cfg/value/yearly-value';
import {ComputationStatusService} from '../../../shared/compute-status.service';
import {SpinnerData} from '../../../shared/spinner/spinner.component';
import {
    ALGORITHM_NO_ALGORITHM_OLD_CONDO,
    ECEconomicsOldCondoRedistributedPodIncentivationAlgorithm,
    ECEconomicsOldCondoRedistributionSettings
} from 'smag-commons/dto/eco/community';
import {EconomicsConfig} from "smag-commons/cfg";

export interface RedistributedMember {
    member: ECMember;
    pod: ECEconomicsRedistributedPod;
    isConsumer: boolean;
}

export interface RedistributedMembers {
    members: RedistributedMember[];
    total: number;
    errors?: EconomicsError[];
}

export interface CappedVAIRedistributionPODSettings {
    cap?: number;
}

const MEMBERS_COLUMN = 'members';
const TOTAL_COLUMN = 'total';
const DETAILS_COLUMN = 'details';
const MEMBERS_SHARE_COLUMN = 'members_share';

const BASE_COLUMNS = [MEMBERS_COLUMN, 'personal_sharing'];
const DEFAULT_COLUMNS = [...BASE_COLUMNS, TOTAL_COLUMN];
const THOUSANDTH_COLUMNS = [...BASE_COLUMNS, 'thousandths', TOTAL_COLUMN, DETAILS_COLUMN];
const ENERGY_AND_PERFORMANCE_COLUMNS = ['energy_sharing', 'performance_incentive'];
const INNER_VAI_COLUMNS = [...ENERGY_AND_PERFORMANCE_COLUMNS, 'fixed_part'];
const INNER_MIXED_COLUMNS = ['mixed_fixed', 'mixed_percentage', 'mixed_performance'];
const VAI_COLUMNS = [...BASE_COLUMNS, ...INNER_VAI_COLUMNS, TOTAL_COLUMN, DETAILS_COLUMN];
const CAPPED_VAI_COLUMNS = [...BASE_COLUMNS, ...INNER_VAI_COLUMNS, 'CAP', TOTAL_COLUMN, 'CAP_settings', DETAILS_COLUMN];
const MIXED_COLUMNS = [...BASE_COLUMNS, ...INNER_MIXED_COLUMNS, TOTAL_COLUMN, DETAILS_COLUMN];
const NULL_REDIST_COLUMNS = [MEMBERS_COLUMN, TOTAL_COLUMN];
const FANO_COLUMNS = [...BASE_COLUMNS, 'absorbed_energy', 'performance_incentive', MEMBERS_SHARE_COLUMN, TOTAL_COLUMN, DETAILS_COLUMN];
const OLD_CONDOM_COLUMNS = [...BASE_COLUMNS, 'virtual_self_consumption', 'tip_fee', 'withdrawal_fee', 'tip_benefit', 'cacv_benefit', 'withdrawal_benefit', 'total_benefit', DETAILS_COLUMN];

const DIALOG_WIDTH = 800;

@Component({
    selector: 'app-ec-economics-redistributed',
    templateUrl: './ec-economics-redistributed.component.html',
    styleUrls: ['./ec-economics-redistributed.component.scss'],
    providers: [
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Members per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class ECEconomicsRedistributedComponent implements OnInit, AfterViewInit, OnDestroy {
    private destroyed = new Subject<void>();
    private reloadPods: Subject<void> = new Subject<void>();
    private reloadMembers: Subject<void> = new Subject<void>();
    private reloadEconomicsData: Subject<void> = new Subject<void>();
    private reloadEconomics: Subject<void> = new BehaviorSubject<void>(null);
    private entityId: string;
    private templateEntity: HierarchyEntity = null;
    private templatePeriod: BillingPeriod = null;
    private templateMembersTotal: number;
    private algorithmSubj: BehaviorSubject<SelectedAlgorithm> = new BehaviorSubject<SelectedAlgorithm>(null);
    private responseErrors: EconomicsError[] = [];
    private readonly nvp: NiceValuePipe;

    @ViewChild('paginator') private paginator: MatPaginator;
    @ViewChild('chooseAlgorithm') private algorithmDialog: TemplateRef<any>;
    @ViewChild('incentiveCalculation') private incentiveCalculationDialog: TemplateRef<any>;
    @ViewChild('changeCap') private capChangeDialog: TemplateRef<any>;
    @ViewChild('details') private detailsDialog: TemplateRef<any>;
    @ViewChild('ecWaitingForApproval') private ecWaitingForApprovalDialog: TemplateRef<any>;
    @ViewChild('spinner') private spinnerDialog: TemplateRef<any>;

    hierarchyEntity: Observable<HierarchyEntity> = null;
    entity: HierarchyEntity;
    timeZone: string = null;
    singleDate: Moment = null;
    response: ECEconomicsRedistributedResponse = null;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = {} as BillingPeriod;
    billingPeriodSubj: ReplaySubject<BillingPeriod> = new ReplaySubject<BillingPeriod>(1);
    algorithm: string = null;
    availableAlgorithms: string[];
    members: BehaviorSubject<RedistributedMembers> = new BehaviorSubject<RedistributedMembers>(null);
    displayedColumns: string[] = DEFAULT_COLUMNS;
    ecMode: ECMode = null;
    futureEc = false;
    inactiveEc = false;
    templateIsDownloading = false;
    searchText = '';
    // searchSubj: BehaviorSubject<string>;
    sharingChart: Partial<ChartOptions>;
    participationChart: Partial<ChartOptions>;
    fanoParticipationChart: Partial<ChartOptions>;
    fixedAmountChart: Partial<ChartOptions>;
    sharingPerformanceChart: Partial<ChartOptions>;
    detailsButtonDisabled = false;
    communityNotConfigured = false;
    isWholeYear = false;
    higherRedistributableWarning = false;
    showComputationWarning = false;
    summaryResponseSubject: BehaviorSubject<YearSummaryConsolidation[]> = new BehaviorSubject<YearSummaryConsolidation[]>([]);
    strategyFeatures: ECEconomicsStrategyFeatures = null;
    strategy: string = null;

    readonly DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;
    readonly DEFAULT_PAGE_SIZES = DEFAULT_PAGE_SIZES;
    readonly ALGORITHM_NO_ALGORITHM = ALGORITHM_NO_ALGORITHM;
    readonly ALGORITHM_THOUSANDTH = ALGORITHM_THOUSANDTH;
    readonly ALGORITHM_MIXED = ALGORITHM_MIXED;
    readonly ALGORITHM_VAI = ALGORITHM_VAI;
    readonly ALGORITHM_CAPPED_VAI = ALGORITHM_CAPPED_VAI;
    readonly ALGORITHM_FANO = ALGORITHM_FANO;
    readonly ALGORITHM_CONDO_OLD = ALGORITHM_CONDO_OLD;
    readonly THOUSANDTHS = THOUSANDTHS;
    readonly CAP = CAP_KEY;
    readonly CACV_STRING = CACV_STRING;
    readonly ECMode = ECMode;
    readonly MEMBERS_SHARE_COLUMN = MEMBERS_SHARE_COLUMN;
    readonly ECPodDataSource = ECPodDataSource;
    readonly ECSurplusPremiumRateScope = ECSurplusPremiumRateScope;
    readonly ECMemberSubjectClassification = ECMemberSubjectClassification;
    readonly getSubjectName = getSubjectName;
    readonly isViewable = isViewable;
    readonly isUpdatable = isUpdatable;
    readonly isDefined = isDefined;
    readonly ECEconomicsFeatures = ECEconomicsFeatures;


    constructor(private readonly router: Router,
                private readonly configurationService: ConfigurationService,
                private readonly msgBoxService: MsgBoxService,
                private readonly i18n: I18NService,
                private readonly hierarchyService: HierarchyService,
                private readonly dialogService: DialogService,
                private readonly apiService: SmagApiService,
                private readonly computationService: ComputationStatusService,
                private readonly changeDetectorRef: ChangeDetectorRef,
                private readonly dialog: MatDialog) {
        this.nvp = new NiceValuePipe(i18n);
        // this.searchSubj = new BehaviorSubject<string>(null);
    }


    ngOnInit() {
        this.configurationService.timeConfig.pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        this.computationService.showComputingWarning.pipe(
            takeUntil(this.destroyed)
        ).subscribe(bool => this.showComputationWarning = bool);

        // NOTE: as we store time range in URL, we need to avoid "navigation" to same entity when
        //       setting URL parameters for time-range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );
        this.hierarchyEntity.pipe(
            filter(he => he !== null),
            takeUntil(this.destroyed)
        ).subscribe(he => {
            this.entity = he;
            this.entityId = he.id;
            if (can(he.permissions, Permission.READ)) {
                this.loadECMode(he.id);
            }
        });

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) : of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            this.billingPeriodCfg = {
                pastDate: entity ? (entity as EnergyCommunity).activation_date || null : null,
                futureDate: null
            };
            if (!isDefined(this.billingPeriodCfg.pastDate)) {
                this.inactiveEc = true;
                this.showWaitingForApprovalModal();
            } else {
                this.inactiveEc = false;
                if (this.billingPeriodCfg.pastDate > moment.tz(this.timeZone).valueOf()) {
                    this.futureEc = true;
                    this.showWaitingForApprovalModal();
                } else {
                    this.futureEc = false;
                }
            }
        });
    }

    ngAfterViewInit() {
        combineLatest([
            this.hierarchyEntity,
            this.billingPeriodSubj,
            this.configurationService.timeConfig,
            this.configurationService.economicsConfig,
            this.algorithmSubj,
            this.reloadEconomics,
            // this.searchSubj.pipe(debounceTime(DEBOUNCE_MILLIS))
        ]).pipe(takeUntil(this.destroyed))
            .subscribe(([hierarchyEntity,
                            billingPeriod,
                            timeCfg,
                            ecoCfg,
                            algo,
                            // searchText
                        ]) => {
                // TODO use searchText to filter member by name when be supports it
                const timeZone = timeCfg.time_zone;
                this.mkAvailableAlgorithms(hierarchyEntity, billingPeriod, ecoCfg);
                this.singleDate = moment.tz(billingPeriod.fromTime, timeZone);
                this.billingPeriod = billingPeriod;
                this.mkData(hierarchyEntity, billingPeriod);
            });

        merge(this.paginator.page,
            this.reloadPods.pipe(tap(() => this.paginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() => combineLatest([this.hierarchyEntity, this.billingPeriodSubj])),
            switchMap(([hierarchyEntity, billingPeriod]) => {
                    this.templateEntity = hierarchyEntity;
                    this.templatePeriod = billingPeriod;
                    this.reloadMembers.next();  // avoids multiples of the subsequent calls
                    return this.computationService
                        .checkComputationStatus(this.entity.id, moment.tz(billingPeriod.fromTime, this.timeZone).startOf('month').year())
                        .pipe(takeUntil(merge(this.destroyed, this.reloadMembers)),
                            switchMap(() => {
                                return this.paginator ? this.mkMembers(hierarchyEntity, sanityFromTimeActivationDate(billingPeriod.fromTime,
                                        this.billingPeriodCfg.pastDate, this.timeZone),
                                    sanityToTime(this.timeZone, billingPeriod.toTime),
                                    this.paginator.pageIndex + 1, this.paginator.pageSize) : of(null as RedistributedMembers)
                                    .pipe(startWith(null as RedistributedMembers), // triggers spinner
                                        catchError(err => {
                                            this.msgBoxService.error(this.i18n.translate('An error occurred while retrieving members'), err);
                                            return of(null as RedistributedMembers);
                                        }));
                            }));
                }
            ),
            takeUntil(this.destroyed),
            switchMap(members => combineLatest([
                this.billingPeriodCfg.pastDate ?
                    mkConsolidatedMonths(this.entityId, this.billingPeriodCfg.pastDate, this.timeZone, this.apiService) : of(null),
                of(members)]))
        ).subscribe(([consolidation, members]) => {
            if (consolidation) {
                this.summaryResponseSubject.next(consolidation);
            }
            if (members) {
                this.templateMembersTotal = members.total;
                this.members.next(members);
            }
        });
        this.changeDetectorRef.detectChanges();
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.isWholeYear = isWholeYearSelected(period.fromTime, period.toTime, this.timeZone, period.type);
        this.router.navigate([],
            {
                queryParams: mkBillingPeriodParams(period),
                queryParamsHandling: 'merge'
            }).then();
    }

    canChooseAlgorithm(entity: HierarchyEntity) {
        if (this.strategy === STRATEGY_OLD_CONDO) {
            // ignore configuration error with old strategy
            return !this.isWholeYear || !isUpdatable(entity) || (this.performanceTotalPods === 0 || !this.performanceTotalPods);
        }
        return !this.isWholeYear || !isUpdatable(entity) || (this.performanceTotalPods === 0 || !this.performanceTotalPods) || this.communityNotConfigured;
    }

    onClickEditAlgorithm() {
        this.dialogService.open<any, SelectedAlgorithm>({
            template: this.algorithmDialog,
            context: {}
        }, {
            width: GIGA_CHAD_DIALOG_MAX_WIDTH,
        }).closed.then(algorithm => {
            if (algorithm) {
                const spinner = this.openSpinner('We are updating the algorithm');
                forkJoin([
                    this.apiService.setYearlyValue(mkYearlyValue(REDISTRIBUTION_SETTINGS,
                        this.mkECEconomicsRedistributionSettings(algorithm), this.entityId, this.singleDate.year())),
                    this.apiService.setYearlyValue(mkYearlyValue(SURPLUS_PREMIUM_RATE_MODE,
                        this.mkYearlyValueSettingsForExceedingTip(algorithm), this.entityId, this.singleDate.year()))
                ]).pipe(catchError(err => {
                    spinner.context.next({
                        loading: false,
                        loadingMessage: this.i18n.translate(mkAlgorithmSelectionErrorMessage(err)),
                        abort: true
                    });
                    this.msgBoxService.error(this.i18n.translate(mkAlgorithmSelectionErrorMessage(err)), err);
                    throw err;
                })).subscribe(() => {
                    // Template only exists on thousandths and mixed algorithms
                    // If no Excel file is bound to the algorithm, reload the pods
                    if (!algorithm.template) {
                        this.postAlgorithmFunction(algorithm);
                        spinner.context.next({
                            loading: false,
                            loadingMessage: this.i18n.translate('Algorithm changed successfully'),
                        });
                        return;
                    }
                    // Wait a set amount of time before asking for computation status, then send the Excel file
                    setTimeout(() => {
                        this.computationService.checkComputationStatus(this.entityId,
                            moment.tz(this.billingPeriod.fromTime, this.timeZone).startOf('month').year())
                            .pipe(
                                takeUntil(merge(this.destroyed, this.reloadEconomicsData)),
                                catchError(err => {
                                    spinner.context.next({
                                        loading: false,
                                        loadingMessage: null,
                                        abort: true
                                    });
                                    this.msgBoxService.error(this.i18n.translate(mkAlgorithmSelectionErrorMessage(err)), err);
                                    throw err;
                                }))
                            .subscribe(() => {
                                this.apiService.setECAlgorithmFile(this.entityId, algorithm.isThousandthsAlgo ? EC_FILE_THOUSANDTHS : EC_FILE_MIXED,
                                    this.singleDate.year(), this.singleDate.month() + 1,
                                    {content: algorithm.template.file, filename: algorithm.template.name})
                                    .then(() => {
                                        this.postAlgorithmFunction(algorithm);
                                        spinner.context.next({
                                            loading: false,
                                            loadingMessage: this.i18n.translate('Algorithm changed successfully'),
                                        });
                                    })
                                    .catch(err => {
                                        spinner.context.next({
                                            loading: false,
                                            loadingMessage: this.i18n.translate(mkAlgorithmSelectionErrorMessage(err)),
                                            abort: true
                                        });
                                        this.msgBoxService.error(this.i18n.translate(mkAlgorithmSelectionErrorMessage(err)), err);
                                    });
                            });
                    }, 1000);
                });
            }
        });
    }

    postAlgorithmFunction(algorithm: SelectedAlgorithm) {
        // this.msgBoxService.info(this.i18n.translate('Algorithm chosen correctly'));
        this.algorithmSubj.next(algorithm);
        this.reloadPods.next();
    }

    onClickIncentiveCalculation(algorithm: string) {
        this.dialog.open(this.incentiveCalculationDialog, {
            data: {algorithm},
            width: `${DEFAULT_DIALOG_MAX_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    // onClickClearSearchText() {
    //     this.searchText = '';
    //     this.paginator.firstPage();
    //     this.searchSubj.next(this.searchText);
    // }

    // onClickSearchText() {
    //     const search = this.cleanSearchText;
    //     if (search) {
    //         this.paginator.firstPage();
    //     }
    //     this.searchSubj.next(search);
    // }

    showWaitingForApprovalModal() {
        this.dialog.open(this.ecWaitingForApprovalDialog, {
            maxWidth: `${DEFAULT_DIALOG_MAX_WIDTH}px`,
            minWidth: `${DEFAULT_DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe();
    }

    exportEconomicsToExcel() {
        this.templateIsDownloading = true;
        this.apiService.exportEconomicsToExcel(ECEconomicsTemplateType.REDISTRIBUTABLE,
            this.templateEntity.id,
            this.templatePeriod.fromTime,
            this.templatePeriod.toTime,
            this.entity.name,
            1,
            this.templateMembersTotal)
            .subscribe(([data, filename]) => {
                downloadBlob(data, filename);
                this.templateIsDownloading = false;
                this.msgBoxService.info(this.i18n.translate('File downloaded!'));
            }, error => {
                this.templateIsDownloading = false;
                this.msgBoxService.error(this.i18n.translate('An error occurred while fetching the template'));
            });
    }

    onClickEditCap(pod: ECEconomicsRedistributedPod) {
        this.dialogService.open<any, CAP>({
            template: this.capChangeDialog,
            context: {pod},
            title: this.i18n.translate('Set up the CAP threshold')
        }, {
            width: CHAD_DIALOG_MAX_WIDTH
        }).closed.then(cap => {
            if (cap) {
                let yearlyValue: YearlyValue;
                if (isNumber(cap.threshold)) {
                    yearlyValue = {
                        name: REDISTRIBUTION_SETTINGS,
                        object_id: cap.POD,
                        object_type: ValueObjectType.EC_POD,
                        year: cap.year,
                        value: {cap: cap.threshold / 100},
                    } as YearlyValue;
                } else {
                    yearlyValue = {
                        name: REDISTRIBUTION_SETTINGS,
                        object_id: cap.POD,
                        object_type: ValueObjectType.EC_POD,
                        year: cap.year,
                        value: {},
                    } as YearlyValue;
                }
                this.apiService.setYearlyValue(yearlyValue).then(() => {
                        this.msgBoxService.info(this.i18n.translate('CAP set up correctly'));
                        this.reloadPods.next();
                        this.reloadEconomics.next();
                    }
                ).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while setting CAP'), err));
            }
        });
    }

    showDetailsDialog(member: RedistributedMember) {
        this.detailsButtonDisabled = true;
        this.apiService.findEntityByPOD(member.pod.pod).pipe(
            switchMap(resp => forkJoin([this.apiService.getECPod(this.entity.id, resp.id),
                of(this.entity.children.find(child => child.id === resp.id))])))
            .subscribe(([pod, podEntity]) => {
                this.mkCharts(this.nvp, member.pod);
                this.dialog.open(this.detailsDialog, {
                    maxWidth: `${CHAD_DIALOG_MAX_WIDTH}px`,
                    minWidth: `${CHAD_DIALOG_MIN_WIDTH}px`,
                    hasBackdrop: true,
                    data: {
                        member,
                        podType: getPodType(podEntity, pod.pod as Pod),
                        podName: pod.name
                    }
                }).afterClosed().subscribe(() => this.detailsButtonDisabled = false);
            });
    }

    resetCap(pod: ECEconomicsRedistributedPod, date: Moment): CAP {
        return {
            POD: pod.pod,
            threshold: null,
            year: date.year(),
            month: date.month() + 1
        } as CAP;
    }

    getPersonalSharingCellClass(pod: ECEconomicsRedistributedPod) {
        if (pod.data_source === ECPodDataSource.STATISTICAL) {
            return 'base';
        } else if (pod.data_source === ECPodDataSource.FIELD) {
            if (pod.incentivation.performance === ECEconomicsPerformance.GOOD) {
                return 'good';
            } else if (pod.incentivation.performance === ECEconomicsPerformance.MEDIUM) {
                return 'medium';
            } else if (pod.incentivation.performance === ECEconomicsPerformance.BAD) {
                return 'bad';
            }
        }
        return '';
    }

    getPersonalSharingCellIcon(pod: ECEconomicsRedistributedPod) {
        return this.getPersonalSharingCellClass(pod) + '-icon';
    }

    getAbsorptionParticipation(pod: ECEconomicsRedistributedPod) {
        return pod.relevant_energy * 100 / this.response.shared_energy;
    }

    getFanoAbsorptionParticipation(pod: ECEconomicsRedistributedPod) {
        const algorithm = pod.incentivation.algorithm as ECEconomicsFanoRedistributedPodIncentivationAlgorithm;
        return isDefined(algorithm.performance) ? algorithm.performance * 100 : 0;
    }

    getTotalBenefit(algorithm: ECEconomicsOldCondoRedistributedPodIncentivationAlgorithm) {
        return algorithm.tip_benefit + algorithm.cacv_benefit + algorithm.sold_energy_benefit;
    }

    get showNullData(): boolean {
        return showNullData(this.inactiveEc, this.futureEc);
    }

    get keepYourSecrets(): boolean {
        if (this.strategy === STRATEGY_OLD_CONDO) {
            // ignore configuration errors on old condo
            return keepYourSecrets(this.inactiveEc, this.futureEc, false);
        }
        return keepYourSecrets(this.inactiveEc, this.futureEc, this.communityNotConfigured);
    }

    get cellTextColor() {
        return this.response.gse ? 'txt-b-16-7' : 'txt-p-16-7';
    }

    get showMembersWarning() {
        return this.response.warnings?.some(w => w.code === REDISTRIBUTION_WARNING_PARTIALLY_RELEVANT_MEMBERS);
    }

    get performanceTotalPods() {
        return this.response.performance.statistical_pods + this.response.performance.field_pods;
    }

    get standardWidth(): number {
        return getAlgoDialogWidth(this.algorithm);
    }

    // get cleanSearchText() {
    //     return this.searchText.trim();
    // }

    private getPersonalSharing(pod: ECEconomicsRedistributedPod) {
        return pod.relevant_energy * 100 / pod.absorbed_energy;
    }

    private mkECEconomicsRedistributionSettings(selectedAlgorithm: SelectedAlgorithm): ECEconomicsRedistributionSettings {
        if (selectedAlgorithm.type === ALGORITHM_THOUSANDTH) {
            return {algorithm: selectedAlgorithm.type} as ECEconomicsThousandthRedistributionSettings;
        } else if (selectedAlgorithm.type === ALGORITHM_VAI) {
            return {
                algorithm: selectedAlgorithm.type,
                fixed_part: selectedAlgorithm.fixedPart,
                split_consumers: selectedAlgorithm.splitConsumers
            } as ECEconomicsVAIRedistributionSettings;
        } else if (selectedAlgorithm.type === ALGORITHM_CAPPED_VAI) {
            return {
                algorithm: selectedAlgorithm.type,
                fixed_part: selectedAlgorithm.fixedPart,
                split_consumers: selectedAlgorithm.splitConsumers,
                global_cap_enabled: selectedAlgorithm.globalCapEnabled,
                global_cap: selectedAlgorithm.globalCap
            } as ECEconomicsCappedVAIRedistributionSettings;
        } else if (selectedAlgorithm.type === ALGORITHM_MIXED) {
            return {
                algorithm: selectedAlgorithm.type
            } as ECEconomicsMixedRedistributionSettings;
        } else if (selectedAlgorithm.type === ALGORITHM_FANO) {
            return {
                algorithm: selectedAlgorithm.type,
                founders_percentage: selectedAlgorithm.foundersPercentage,
                social_fund_percentage: selectedAlgorithm.socialFundPercentage,
                plants_percentage: selectedAlgorithm.plantsPercentage
            } as ECEconomicsFanoRedistributionSettings;
        } else if (selectedAlgorithm.type === ALGORITHM_CONDO_OLD) {
            return {
                algorithm: selectedAlgorithm.type,
                shared_energy_fee_value: selectedAlgorithm.tipFee,
                rei_fee_value: selectedAlgorithm.withdrawalFee,
                tip_benefit_value: selectedAlgorithm.tipBenefit,
                sold_energy_benefit_value: selectedAlgorithm.withdrawalBenefit,
                cacv_benefit_value: selectedAlgorithm.cacvBenefit
            } as ECEconomicsOldCondoRedistributionSettings;
        }
        // TODO add other algorithms when supported
    }

    private mkYearlyValueSettingsForExceedingTip(selectedAlgorithm: SelectedAlgorithm): ECSurplusPremiumRate {
        if (isDefined(selectedAlgorithm.willNotRedistribute)) {
            if (selectedAlgorithm.willNotRedistribute) {
                return {
                    mode: ECSurplusPremiumRateScope.ALLOCATED
                };
            } else {
                return {
                    mode: ECSurplusPremiumRateScope.CONSUMER_REDISTRIBUTED
                };
            }
        } else {
            return {
                mode: ECSurplusPremiumRateScope.UNDEFINED
            };
        }
    }

    private loadECMode(networkId: string) {
        this.apiService.communityPodsStatistics(networkId)
            .pipe(map(stats => mkMode(stats)))
            .subscribe(ecMode => this.ecMode = ecMode);
    }

    private mkAvailableAlgorithms(hierarchyEntity: HierarchyEntity,
                                  billingPeriod: BillingPeriod,
                                  ecoCfg: EconomicsConfig) {
        const year = moment.tz(billingPeriod.fromTime, this.timeZone).year();
        this.apiService.getEconomicsStrategyAlgorithms(hierarchyEntity.id, year)
            .subscribe(algorithms => {
                this.availableAlgorithms = ecoCfg.incentive_redistribution_algorithms
                    .filter(algo => algorithms.includes(algo));
            });
    }

    private mkData(entity: HierarchyEntity, billingPeriod: BillingPeriod) {
        this.response = null;
        if (entity?.type === EntityType.ENERGY_COMMUNITY && isViewable(entity)) {
            if (!this.inactiveEc && !this.futureEc) {
                this.reloadEconomicsData.next();
                this.computationService.checkComputationStatus(entity.id, moment.tz(billingPeriod.fromTime, this.timeZone).startOf('month').year())
                    .pipe(takeUntil(merge(this.destroyed, this.reloadEconomicsData)))
                    .subscribe(() => {
                        combineLatest([this.apiService.ecEconomicsRedistributed(entity.id, sanityFromTimeActivationDate(billingPeriod.fromTime,
                            this.billingPeriodCfg.pastDate, this.timeZone), sanityToTime(this.timeZone, billingPeriod.toTime)),
                            this.apiService.getEconomicsStrategyFeatures(entity.id, moment.tz(billingPeriod.fromTime, this.timeZone).year()),
                            this.apiService.getYearlyValue(mkYearlyValue(STRATEGY, null, entity.id, moment.tz(billingPeriod.fromTime, this.timeZone).year())),
                        ]).pipe(takeUntil(merge(this.destroyed, this.reloadEconomicsData)),
                            filter(r => r !== null))
                            .subscribe(([response, strategyFeatures, strategy]) => {
                                this.response = response;
                                this.strategy = strategy.value.strategy_type;
                                this.strategyFeatures = strategyFeatures;
                                this.responseErrors = this.response.errors ? this.response.errors : [];
                                this.communityNotConfigured = this.responseErrors
                                    .filter(err => err.type === EconomicsErrorType.MISSING_PARAM)
                                    .some(err =>
                                        err.metadata[ECO_ERROR_PARAM_KEY] === COSTS ||
                                        err.metadata[ECO_ERROR_PARAM_KEY] === EC_PLANTS_REI_REDISTRIBUTION ||
                                        err.metadata[ECO_ERROR_PARAM_KEY] === PLANT_TIP_QUOTE);
                                if (!response.redistribution) {
                                    this.displayedColumns = NULL_REDIST_COLUMNS;
                                    this.algorithm = null;
                                } else {
                                    this.algorithm = response.redistribution.algorithm;
                                    if (this.algorithm === ALGORITHM_THOUSANDTH) {
                                        this.displayedColumns = THOUSANDTH_COLUMNS;
                                    } else if (this.algorithm === ALGORITHM_VAI) {
                                        this.displayedColumns = VAI_COLUMNS;
                                    } else if (this.algorithm === ALGORITHM_CAPPED_VAI) {
                                        this.displayedColumns = CAPPED_VAI_COLUMNS;
                                    } else if (this.algorithm === ALGORITHM_MIXED) {
                                        this.displayedColumns = MIXED_COLUMNS;
                                    } else if (this.algorithm === ALGORITHM_NO_ALGORITHM) {
                                        this.displayedColumns = DEFAULT_COLUMNS;
                                        if (this.isWholeYear && isUpdatable(entity) && this.performanceTotalPods > 0 && !this.communityNotConfigured) {
                                            this.onClickEditAlgorithm();
                                        }
                                    } else if (this.algorithm === ALGORITHM_NO_ALGORITHM_OLD_CONDO) {
                                        this.displayedColumns = DEFAULT_COLUMNS;
                                        if (this.isWholeYear && isUpdatable(entity) && this.performanceTotalPods > 0) {
                                            this.onClickEditAlgorithm();
                                        }
                                    } else if (this.algorithm === ALGORITHM_FANO) {
                                        this.displayedColumns = FANO_COLUMNS;
                                    } else if (this.algorithm === ALGORITHM_CONDO_OLD) {
                                        this.displayedColumns = OLD_CONDOM_COLUMNS;
                                    }
                                }
                                // Set warning for higher redistributed compared to redistributable in case of mixed_algorithm
                                this.higherRedistributableWarning = response.warnings
                                    .some(warning => warning.code === REDISTRIBUTION_WARNING_MIXED_INSUFFICIENT_REVENUE);
                            });
                    });
            } else {
                this.generateInactiveEcoRedistributedData();
            }
        }
    }

    private generateInactiveEcoRedistributedData() {
        this.response = {
            incentivation: {} as ECEconomicsRedistributedIncentivation,
            performance: {} as ECEconomicsRedistributedPerformance,
            redistribution: {} as ECEconomicsRedistributionSettings,
            warnings: [],
            errors: [],
            absorbed_energy: null,
            effective_injected_energy: null,
            shared_energy: null,
            gse: null,
            surplus_premium_rate: {} as ECEconomicsSurplusPremiumRate
        };
    }

    private mkMembers(entity: HierarchyEntity, fromTime: number, toTime: number,
                      page: number, size: number): Observable<RedistributedMembers> {
        if (entity?.type === EntityType.ENERGY_COMMUNITY && isViewable(entity) && (!this.inactiveEc && !this.futureEc)) {
            return forkJoin([
                this.apiService.ecEconomicsRedistributedMembers(entity.id, fromTime, toTime, page, size),
                this.apiService.getNetwork(entity.id) as Observable<EnergyCommunityNetwork>
            ]).pipe(switchMap(([resp, net]) => resp.members.length ?
                forkJoin(resp.members.map(redistributedMember => (redistributedMember.member_id ?
                    this.apiService.loadMember(redistributedMember.member_id) :
                    of(null as ECMember))
                    .pipe(
                        map(member => [redistributedMember, member] as [ECEconomicsRedistributedMember, ECMember])
                    ))).pipe(map((members) => ({
                    members: mkMember(members, net),
                    total: resp.total,
                    errors: resp.errors
                } as RedistributedMembers))) :
                of({members: [], total: 0} as RedistributedMembers)));
        }
        return of({members: [], total: 0} as RedistributedMembers);
    }

    private mkCharts(nvp: NiceValuePipe, pod: ECEconomicsRedistributedPod) {
        if (this.algorithm === ALGORITHM_THOUSANDTH ||
            this.algorithm === ALGORITHM_VAI ||
            this.algorithm === ALGORITHM_CAPPED_VAI ||
            this.algorithm === ALGORITHM_FANO ||
            this.algorithm === ALGORITHM_CONDO_OLD) {
            this.mkPersonalSharingChart(nvp, pod);
        }
        if (this.algorithm === ALGORITHM_VAI ||
            this.algorithm === ALGORITHM_CAPPED_VAI ||
            this.algorithm === ALGORITHM_CONDO_OLD) {
            this.mkAbsorptionParticipationChart(nvp, pod);
        }
        if (this.algorithm === ALGORITHM_FANO) {
            this.mkFanoAbsorptionParticipationChart(nvp, pod);
        }
        if (this.algorithm === ALGORITHM_MIXED) {
            this.mkFixedPercentageChart(nvp, pod);
            this.mkFixedSharingPerformanceChart(nvp, pod);
        }
    }

    private mkFixedPercentageChart(nvp: NiceValuePipe, pod: ECEconomicsRedistributedPod) {
        // tslint:disable-next-line: no-string-literal
        const val = pod.redistribution['mixed_fixed'] !== null ? (pod.redistribution['mixed_fixed'] * 100) : null;
        this.fixedAmountChart = {
            chart: {type: 'donut', height: 90},
            colors: [environment.appColors.ecoYellow, environment.appColors.ecoEmpty],
            dataLabels: disableDataLabels(),
            apexLegends: disableLegend(),
            plotOptions: {
                pie: {
                    donut: {
                        size: '65%'
                    }
                }
            },
            labels: getTranslatedLabels(['Fixed percentage', ''], this.i18n),
            series: val ? [val, 100 - val] : [0, 0],
            tooltip: getNiceTooltip(nvp)
        };
    }

    private mkFixedSharingPerformanceChart(nvp: NiceValuePipe, pod: ECEconomicsRedistributedPod) {
        // tslint:disable-next-line: no-string-literal
        const val = pod.incentivation.algorithm['performance'] !== null ? (pod.incentivation.algorithm['performance'] * 100) : null;
        this.sharingPerformanceChart = {
            chart: {type: 'donut', height: 90},
            // tslint:disable-next-line: no-string-literal
            colors: [pod.redistribution['mixed_performance'] ? environment.appColors.ecoYellow : environment.appColors.ecoLegend, environment.appColors.ecoEmpty],
            dataLabels: disableDataLabels(),
            apexLegends: disableLegend(),
            plotOptions: {
                pie: {
                    startAngle: -90,
                    endAngle: 90,
                    donut: {
                        size: '65%'
                    }
                }
            },
            labels: getTranslatedLabels(['POD weight on total absorption', ''], this.i18n),
            series: val ? [val, 100 - val] : [0, 0],
            tooltip: getNiceTooltip(nvp),
            subtitle: {
                text: this.nvp.transform(val ? val : 0, '%', 2),
                align: 'center',
                offsetY: 35,
                floating: true,
                style: {
                    fontSize: '12px',
                    fontWeight: '400',
                    fontFamily: environment.fontFamily,
                    color: environment.appColors.ecoDarkLegend
                },
            }
        };
    }

    private mkPersonalSharingChart(nvp: NiceValuePipe, pod: ECEconomicsRedistributedPod) {
        const personalSharing = this.getPersonalSharing(pod);
        this.sharingChart = mkPersonalSharingChart(nvp, this.i18n, personalSharing);
    }

    private mkAbsorptionParticipationChart(nvp: NiceValuePipe, pod: ECEconomicsRedistributedPod) {
        this.participationChart = mkAbsorptionParticipationChart(nvp, this.i18n, this.getAbsorptionParticipation(pod));
    }

    private mkFanoAbsorptionParticipationChart(nvp: NiceValuePipe, pod: ECEconomicsRedistributedPod) {
        this.fanoParticipationChart = mkAbsorptionParticipationChart(nvp, this.i18n, this.getFanoAbsorptionParticipation(pod), 'Participation in community');
    }

    private openSpinner(message: string) {
        const spinnerData = new BehaviorSubject<SpinnerData>({
            loading: true,
            loadingMessage: this.i18n.translate(message)
        });
        return this.dialogService.open<Subject<SpinnerData>, undefined>({
            template: this.spinnerDialog,
            context: spinnerData
        }, {
            width: DIALOG_WIDTH,
            hideCloseButton: true
        });
    }
}

function mkMember(members: [ECEconomicsRedistributedMember, ECMember][],
                  net: EnergyCommunityNetwork): RedistributedMember[] {
    return _.flatten(members.map(([redistMember, ecMember]) => [
        {member: ecMember, pod: null} as RedistributedMember,
        ...redistMember.pods.map(pod => {
            const consumer = isDefined(net.pods[pod.id]) && (isConsumer(net.pods[pod.id]) || isProsumer(net.pods[pod.id]));
            return {member: ecMember, pod, isConsumer: consumer} as RedistributedMember;
        })]));
}

function mkAlgorithmSelectionErrorMessage(err: Error): string {
    // NOTE: economics-server provides its own set of error codes
    const code = errorCode(err);
    switch (code) {
        case EconomicsErrorCode.INVALID_THOUSANDTHS:
            return 'Thousandths must be > 0 and < 1000 for every POD';
        case EconomicsErrorCode.INVALID_TOTAL_THOUSANDTHS:
            return 'Total thousandths must be < 1000';
        case EconomicsErrorCode.INVALID_TOTAL_CAP:
            return 'Total CAP for consuming PODs must be >= 100%';
        case EconomicsErrorCode.INVALID_FIXED_AMOUNT:
            return 'Invalid fixed amount';
        case EconomicsErrorCode.INVALID_FIXED_PERCENTAGE:
            return 'Invalid fixed percentage';
        case EconomicsErrorCode.INVALID_TOTAL_FIXED_INCENTIVE:
            return 'Total fixed incentive has a percentage higher than 100%';
        case EconomicsErrorCode.INVALID_PERFORMANCE_INCENTIVE:
            return 'Invalid performance incentive';
        default:
            return 'An error occurred while choosing the algorithm';
    }
}
